import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../services/api';
import { fetchAllPages } from '../services/paging';
import type { Project, Category, Topic } from '../types';

interface ProjectContextType {
  // Projects
  projects: Project[];
  selectedProject: Project | null;
  selectedProjectId: number | null;
  setSelectedProjectId: (id: number | null) => void;
  loading: boolean;
  refreshProjects: () => Promise<void>;

  // Categories
  categories: Category[];
  selectedCategory: Category | null;
  selectedCategoryId: number | null;
  setSelectedCategoryId: (id: number | null) => void;
  refreshCategories: () => Promise<void>;

  // Topics
  topics: Topic[];
  selectedTopic: Topic | null;
  selectedTopicId: number | null;
  setSelectedTopicId: (id: number | null) => void;
  refreshTopics: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | null>(null);

const STORAGE_KEY = 'jcseo_selected_project_id';
const STORAGE_KEY_CATEGORY = 'jcseo_selected_category_id';
const STORAGE_KEY_TOPIC = 'jcseo_selected_topic_id';

interface ProjectProviderProps {
  children: ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  // Projects state
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : null;
  });
  const [loading, setLoading] = useState(true);

  // Categories state
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_CATEGORY);
    return stored ? parseInt(stored, 10) : null;
  });

  // Topics state
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopicId, setSelectedTopicIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_TOPIC);
    return stored ? parseInt(stored, 10) : null;
  });

  const refreshProjects = useCallback(async () => {
    try {
      const projectsData = await api.getProjects();
      setProjects(projectsData);

      setSelectedProjectIdState((currentId) => {
        if (!currentId && projectsData.length > 0) {
          localStorage.setItem(STORAGE_KEY, projectsData[0].id.toString());
          return projectsData[0].id;
        }
        if (currentId && !projectsData.find(p => p.id === currentId)) {
          if (projectsData.length > 0) {
            localStorage.setItem(STORAGE_KEY, projectsData[0].id.toString());
            return projectsData[0].id;
          } else {
            localStorage.removeItem(STORAGE_KEY);
            return null;
          }
        }
        return currentId;
      });
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  // Refresh categories function - can be called externally
  const refreshCategories = useCallback(async () => {
    if (!selectedProjectId) {
      setCategories([]);
      return;
    }

    try {
      const data = await api.getCategories(selectedProjectId);
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [selectedProjectId]);

  /**
   * Every node in the selected subject.
   *
   * Keyed on the PROJECT, not a category. It used to load "the topics in the
   * selected category", which was the whole story when the tree was exactly two
   * levels — but the workspace stopped setting a category when the two levels
   * became one, so this returned nothing and `selectedTopic` was null for every
   * pane that reads it. A node four deep could not be resolved at all.
   *
   * Paged, because /topics caps `limit` at 100 and refuses more with a 422.
   * One subject here already has 107 nodes, so a single call would silently
   * drop the tail and whichever node you clicked might be in it.
   */
  const refreshTopics = useCallback(async () => {
    if (!selectedProjectId) {
      setTopics([]);
      return;
    }
    try {
      setTopics(
        await fetchAllPages((skip, limit) =>
          api.getTopics(null, selectedProjectId, skip, limit),
        ),
      );
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  }, [selectedProjectId]);

  // Fetch categories when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setCategories([]);
      setSelectedCategoryIdState(null);
      localStorage.removeItem(STORAGE_KEY_CATEGORY);
      setTopics([]);
      setSelectedTopicIdState(null);
      localStorage.removeItem(STORAGE_KEY_TOPIC);
      return;
    }

    const fetchCategories = async () => {
      try {
        const data = await api.getCategories(selectedProjectId);
        setCategories(data);

        // Validate selected category still belongs to this project
        setSelectedCategoryIdState((currentId) => {
          if (currentId && !data.find((c) => c.id === currentId)) {
            localStorage.removeItem(STORAGE_KEY_CATEGORY);
            setTopics([]);
            setSelectedTopicIdState(null);
            localStorage.removeItem(STORAGE_KEY_TOPIC);
            return null;
          }
          return currentId;
        });
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };

    fetchCategories();
  }, [selectedProjectId]);

  // Load the subject's nodes when the subject changes.
  useEffect(() => {
    if (!selectedProjectId) {
      setTopics([]);
      setSelectedTopicIdState(null);
      localStorage.removeItem(STORAGE_KEY_TOPIC);
      return;
    }

    const fetchTopics = async () => {
      try {
        const data = await fetchAllPages((skip, limit) =>
          api.getTopics(null, selectedProjectId, skip, limit),
        );
        setTopics(data);

        // Drop a remembered node that is not in this subject.
        setSelectedTopicIdState((currentId) => {
          if (currentId && !data.find((t) => t.id === currentId)) {
            localStorage.removeItem(STORAGE_KEY_TOPIC);
            return null;
          }
          return currentId;
        });
      } catch (err) {
        console.error('Failed to fetch topics:', err);
      }
    };

    fetchTopics();
  }, [selectedProjectId]);

  const setSelectedProjectId = useCallback((id: number | null) => {
    setSelectedProjectIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    // Clear downstream selections
    setSelectedCategoryIdState(null);
    localStorage.removeItem(STORAGE_KEY_CATEGORY);
    setSelectedTopicIdState(null);
    localStorage.removeItem(STORAGE_KEY_TOPIC);
  }, []);

  const setSelectedCategoryId = useCallback((id: number | null) => {
    setSelectedCategoryIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY_CATEGORY, id.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY_CATEGORY);
    }
    // Clear downstream selection
    setSelectedTopicIdState(null);
    localStorage.removeItem(STORAGE_KEY_TOPIC);
  }, []);

  const setSelectedTopicId = useCallback((id: number | null) => {
    setSelectedTopicIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY_TOPIC, id.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY_TOPIC);
    }
  }, []);

  const selectedProject = projects.find(p => p.id === selectedProjectId) || null;
  const selectedCategory = categories.find(c => c.id === selectedCategoryId) || null;
  const selectedTopic = topics.find(t => t.id === selectedTopicId) || null;

  const value: ProjectContextType = {
    projects,
    selectedProject,
    selectedProjectId,
    setSelectedProjectId,
    loading,
    refreshProjects,
    categories,
    selectedCategory,
    selectedCategoryId,
    setSelectedCategoryId,
    refreshCategories,
    topics,
    selectedTopic,
    selectedTopicId,
    setSelectedTopicId,
    refreshTopics,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextType {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}
